import { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
  title?: string;
  description?: string;
  reloadLabel?: string;
  showErrorDetails?: boolean;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className='min-h-screen flex items-center justify-center bg-brand-pale p-4'>
          <div className='max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center'>
            <div className='w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4'>
              <svg className='w-8 h-8 text-red-600' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z'
                />
              </svg>
            </div>
            <h2 className='text-2xl font-bold text-brand-gray mb-2'>{this.props.title ?? 'Something went wrong'}</h2>
            <p className='text-gray-600 mb-6'>
              {this.props.description ??
                "We're sorry, but something unexpected happened. Please try reloading the page."}
            </p>
            {this.props.showErrorDetails && this.state.error && (
              <pre className='text-xs text-left bg-gray-100 p-3 rounded mb-4 overflow-auto max-h-32'>
                {this.state.error.message}
              </pre>
            )}
            <button
              onClick={this.handleReload}
              className='px-6 py-3 bg-primary text-white font-bold rounded hover:bg-primary-dark transition'>
              {this.props.reloadLabel ?? 'Reload Page'}
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
